// ============================================================================
// Agro Trade Global — Product Catalogue
// ----------------------------------------------------------------------------
// EDITING TIP: To add a photo to any product, paste the image URL into the
// `image` field (currently `null`). The UI will automatically swap the
// placeholder frame for your real photo. Everything else (layout, motion,
// captions) stays the same.
//
// Recommended image aspect: landscape (e.g. 1200x900 or 4:3). JPG/PNG/WEBP.
// ============================================================================

// --------------------- ANIMAL FEED — DDGS (hero products) -------------------
export const DDGS_INTRO =
  "A remarkable transformation of maize and rice into a nutrient-rich feed ingredient, DDGS delivers a powerful blend of protein, fibre, energy, and essential nutrients. It enhances modern feed formulations by supporting improved digestibility, efficient growth, and consistent animal performance. Built for reliability, it brings exceptional nutritional value to poultry, dairy, and livestock nutrition.";

export const DDGS_HERO = [
  {
    id: "pure-maize-ddgs",
    name: "Pure Maize DDGS",
    subtitle: "DDGS",
    image: "/products/feed/pure-maize-ddgs.png", // Add photo URL here
    description:
      "A powerful by-product of the golden maize grain, Maize DDGS is naturally enriched with protein, energy, and digestible fibre. Widely valued in poultry, dairy, and livestock nutrition, it enhances feed formulations by improving nutritional density, palatability, and overall animal performance.",
  },
  {
    id: "pure-rice-ddgs",
    name: "Pure Rice DDGS",
    subtitle: "DDGS",
    image: "/products/feed/pure-rice-ddgs.png",
    description:
      "Rich in protein, fibre, and essential nutrients, Rice DDGS is a functional ingredient designed to strengthen modern animal feed formulations. Used in poultry, cattle, and aquaculture diets, it contributes to improved digestibility, energy utilization, and overall feed performance.",
  },
];

// --------------------------- MAIZE & ITS DERIVATIVES ------------------------
export const MAIZE_DERIVATIVES = [
  {
    id: "maize",
    name: "Maize",
    subtitle: "The Golden Grain of Agriculture",
    image: "/products/feed/maize.png",
    description:
      "Premium yellow maize embodies the perfect balance of nourishment and performance and is the foundation of world-class animal nutrition. Carefully sourced for purity, uniformity, and quality, our maize delivers the reliability trusted by feed manufacturers and distributors worldwide.",
  },
  {
    id: "cgf",
    name: "CGF",
    subtitle: "Corn Gluten Feed",
    image: "/products/feed/cgf.png",
    description:
      "Produced through carefully controlled processing, corn gluten feed offers an ideal balance of digestible fibre, protein, and energy. A trusted ingredient for ruminant nutrition, promoting healthier digestion and consistent performance.",
  },
  {
    id: "cgm",
    name: "CGM",
    subtitle: "Corn Gluten Meal",
    image: "/products/feed/cgm.png",
    description:
      "A premium high-protein ingredient recognized for its excellent amino acid profile and digestibility. Widely preferred by feed manufacturers seeking improved muscle development, efficient weight gain, and superior feed conversion ratios.",
  },
  {
    id: "corn-doc",
    name: "DOC",
    subtitle: "De-Oiled Cake",
    image: "/products/feed/corn-doc.png",
    description:
      "A golden source of concentrated nutrition, premium corn de-oiled cake delivers valuable protein and fibre for balanced animal feed formulations. We ensure a consistent, superior-grade ingredient that supports healthier growth and enhanced feed performance.",
  },
  {
    id: "corn-ddgs-doc",
    name: "Corn DDGS DOC",
    subtitle: "DDGS De-Oiled Cake",
    image: "/products/feed/corn-ddgs-doc.png",
    description:
      "Created through advanced processing of maize-based distillers\u2019 grains, our premium DDGS de-oiled cake is a powerful source of protein, fibre, and concentrated nutrition. Selected for superior quality and consistency, it helps feed manufacturers achieve balanced formulations and optimized animal performance.",
  },
  {
    id: "csl",
    name: "CSL",
    subtitle: "Corn Steep Liquor",
    image: "/products/feed/csl.png",
    description:
      "A nutrient-rich ingredient valued for its naturally occurring proteins, amino acids, vitamins, and minerals. Extensively utilized in feed manufacturing and fermentation processes to enhance nutritional efficiency and product performance.",
  },
  {
    id: "corn-starch",
    name: "Corn Starch",
    subtitle: "Precision-Processed",
    image: "/products/feed/corn-starch.png",
    description:
      "Crafted from quality maize through precision processing, our corn starch delivers exceptional purity, functionality, and consistency. Every batch meets exacting standards for reliable performance across diverse applications.",
  },
  {
    id: "corn-oil",
    name: "Corn Oil",
    subtitle: "Refined & Pure",
    image: "/products/feed/corn-oil.png",
    description:
      "Extracted from carefully selected maize, our corn oil reflects purity, consistency, and superior quality. Through trusted sourcing and stringent quality checks, we deliver a refined ingredient that meets the highest standards of performance and reliability.",
  },
];

// ----------------------------- RICE DERIVATIVES ------------------------------
export const RICE_DERIVATIVES = [
  {
    id: "dorb",
    name: "DORB",
    subtitle: "De-Oiled Rice Bran",
    image: "/products/feed/dorb.png",
    description:
      "A naturally crafted source of balanced nutrition, de-oiled rice bran enriches animal feed with valuable protein, fibre, and essential nutrients. Carefully selected and processed, it brings consistency, digestibility, and superior feed performance.",
  },
  {
    id: "rice-bran-oil",
    name: "Rice Bran Oil",
    subtitle: "Refined",
    image: "/products/feed/rice-bran-oil.png",
    description:
      "A refined expression of nature\u2019s finest grain, rice bran oil combines elegance, purity, and nutritional value. Crafted with attention to quality at every stage, we deliver a versatile ingredient that adds value to modern feed solutions.",
  },
  {
    id: "crude-rice-bran-oil",
    name: "Crude Rice Bran Oil",
    subtitle: "Golden Essence",
    image: "/products/feed/crude-rice-bran-oil.png",
    description:
      "The golden essence of rice bran, crude rice bran oil delivers concentrated energy to enhance the nutritional value of animal feed. Sourced with precision and quality assurance, it supports efficient formulations and improved animal vitality.",
  },
];

// -------------------------- OIL SEEDS DERIVATIVES ----------------------------
export const OILSEEDS_DERIVATIVES = [
  {
    id: "mustard-doc",
    name: "Mustard DOC",
    subtitle: "Plant-Based Protein",
    image: "/products/feed/mustard-doc.png",
    description:
      "A naturally powerful source of plant-based nutrition, mustard de-oiled cake enriches animal feed with quality protein and essential nutrients. Carefully processed to preserve its nutritional strength, it supports balanced diets, healthy growth, and improved feed performance.",
  },
  {
    id: "soya-doc",
    name: "Soya DOC",
    subtitle: "High-Protein Cornerstone",
    image: "/products/feed/soya-doc.png",
    description:
      "A cornerstone of modern animal nutrition, soya de-oiled cake delivers a rich source of high-quality protein for advanced feed formulations. Selected for superior consistency and nutritional value, it helps enhance growth, productivity, and overall animal performance.",
  },
  {
    id: "sesame-doc",
    name: "Sesame DOC",
    subtitle: "Nutritional Depth",
    image: "/products/feed/sesame-doc.png",
    description:
      "A naturally enriched feed ingredient, sesame cake carries the nutritional goodness of sesame seeds with valuable protein and energy content. Carefully sourced and prepared, it adds depth, quality, and nutritional balance to animal feed formulations.",
  },
  {
    id: "rapeseed-doc",
    name: "Rapeseed DOC",
    subtitle: "Balanced Nutrition",
    image: "/products/feed/rapeseed-doc.png",
    description:
      "Derived from carefully processed rapeseed, this nutrient-rich ingredient brings valuable protein and fibre to balanced animal feed solutions. With a focus on quality and reliability, it contributes to efficient nutrition and sustainable livestock performance.",
  },
];

// --------------------------------- SPICES ------------------------------------
export const TURMERIC = {
  hero: {
    id: "lakadong-turmeric",
    name: "Lakadong Turmeric",
    badge: "3X High Curcumin (7%+)",
    image: "/products/spices/lakadong-turmeric.png",
    description:
      "Grown in the misty hills of Meghalaya without chemicals or synthetic fertilizers, Lakadong turmeric is the gold standard when it comes to curcumin content. It\u2019s the preferred choice for nutraceutical, pharmaceutical, and premium food brands worldwide.",
  },
  varieties: [
    {
      id: "black-turmeric",
      name: "Black Turmeric",
      image: "/products/spices/black-turmeric.png",
      description:
        "Renowned for its dark-hued rhizome and unique aromatic profile, it stands out as one of North-East India\u2019s most exceptional botanicals. Its potent bioactive compounds and versatile uses in wellness, herbal formulations, and high-end cuisine make it highly sought after globally.",
    },
    {
      id: "rajender-sonia",
      name: "Rajender Sonia",
      badge: "Curcumin 5.5\u20136%",
      image: "/products/spices/rajender-sonia.png",
      description:
        "A rare and distinctive variety, celebrated for its vibrant golden-orange colour, fine fibrous texture, and high curcumin content.",
    },
    {
      id: "megha-1",
      name: "Megha-1",
      badge: "Curcumin 2\u20133%",
      image: "/products/spices/megha-1.png",
      description:
        "Deep golden hue, uniform rhizome structure, and concentrated curcumin levels.",
    },
    {
      id: "dried-turmeric-fingers",
      name: "Dried Turmeric Fingers",
      badge: "Curcumin 1\u20132%",
      image: "/products/spices/dried-turmeric-fingers.png",
      description:
        "Traditionally cured turmeric fingers, prized for their earthy warmth and versatility across culinary and industrial applications.",
    },
  ],
  variants: "Dried & Sliced | Powdered | Bulk & Custom packaging available",
};

export const RED_CHILLI = {
  hero: {
    id: "bhut-jolokhia",
    name: "Bhut Jolokhia",
    badge: "GI-Tagged \u00b7 Ghost Pepper",
    image: "/products/spices/bhut-jolokhia.png",
    description:
      "One of the hottest chillies on the planet. Nagaland\u2019s legendary Bhut Jolokhia isn\u2019t just about the heat \u2014 it\u2019s about the story. This GI-tagged product is prized by hot sauce makers, pharmaceutical companies, and adventurous chefs. One batch changes how your customers experience spice.",
  },
  varieties: [
    {
      id: "tepa",
      name: "Tepa",
      image: "/products/spices/tepa.png",
      description:
        "Vibrant colour, balanced heat, and a rich, aromatic flavour that echoes the vibrant heat and aromatic depth of South Indian chillies. Its unique North-Eastern terroir adds depth and nuance, elevating spice blends and culinary creations alike.",
    },
    {
      id: "balijuri",
      name: "Balijuri",
      image: "/products/spices/balijuri.png",
      description:
        "Captivates with its vibrant red colour and fiery, yet balanced, heat. This distinctive variety brings intensity and character to spice blends, sauces, gourmet spice creations and traditional dishes alike.",
    },
    {
      id: "birds-eye",
      name: "Bird\u2019s Eye",
      image: null,
      description:
        "Midget among chillies, intensely flavourful, formidable in heat \u2014 Bird\u2019s Eye Chilli from North-East India is a specialty spice celebrated for its fiery punch and vibrant aroma. A unique treasure for high-quality spice blends.",
    },
    {
      id: "dalle",
      name: "Dalle",
      image: "/products/spices/dalle.png",
      description:
        "A culinary gem renowned for its bold striking heat, complex fruity aroma, and rich, smoky undertones. Its bold flavour and rarity have earned it acclaim in international gourmet and spice markets.",
    },
  ],
  variants: "Dried | Fresh | Powdered | Custom grades",
};

export const GINGER = {
  intro:
    "North-East India, with its fertile soil and pristine climate, produces some of the finest quality ginger \u2014 known for its strong aroma, rich oils, and high fibre content. Revered in both culinary and wellness traditions, ginger from North-East India embodies purity, richness, and a legacy of natural excellence, finding wide use in food, beverages, nutraceuticals, and traditional wellness products.",
  strengths: [
    "Chemical-Free Cultivation",
    "Rich in Essential Oils & Antioxidants",
  ],
  regions: [
    "Assam",
    "Nagaland",
    "Karbi Anglong",
    "Meghalaya",
    "Mizoram",
    "Arunachal Pradesh",
    "Manipur",
  ],
  blackGinger: {
    id: "black-ginger",
    name: "Black Ginger",
    image: "/products/spices/black-ginger.png",
    description:
      "One of nature\u2019s most powerful herbal ingredients, valued for its rich concentration of bioactive compounds. Used actively in nutraceutical, herbal supplement, and functional food industries.",
  },
  variants: "Dried & Sliced | Fresh | Powdered | Bulk & Custom Packaging",
  image: "/products/spices/ginger.png", // regional ginger hero photo
};

export const WHOLE_SPICES = [
  {
    id: "bayleaf",
    name: "Bayleaf",
    image: null,
    description:
      "Quietly aromatic and elegantly subtle, our Assam Bay Leaves unfold layers of earthy warmth with every simmer. An essential ingredient for chefs who appreciate depth, balance, and understated sophistication.",
  },
  {
    id: "cinnamon",
    name: "Cinnamon",
    image: null,
    description:
      "Warm, naturally sweet, and beautifully fragrant, our Cinnamon is crafted to leave a lasting impression. Its delicate complexity brings effortless refinement to desserts, beverages, and savoury creations alike.",
  },
  {
    id: "cardamom",
    name: "Cardamom",
    image: null,
    description:
      "Small in size yet unmistakably expressive, our Cardamom reveals bright citrus notes wrapped in an intensely floral aroma. A timeless spice chosen to transform everyday recipes into extraordinary experiences.",
  },
  {
    id: "black-pepper",
    name: "Black Pepper",
    image: null,
    description:
      "Bold without overpowering, our Black Pepper offers a vibrant burst of spice with remarkable depth and clarity. Every peppercorn is selected to celebrate the purity and precision of exceptional flavour.",
  },
  {
    id: "star-anise",
    name: "Star Anise",
    image: null,
    description:
      "Sculpted by nature, Star Anise is as captivating in appearance as it is in aroma. Its naturally sweet, spice-laced character lends an unmistakable elegance to gourmet cuisine and artisanal infusions.",
  },
];

// ------------------------------ ASSAM TEA ------------------------------------
export const TEA = {
  intro:
    "They say \u2018you haven\u2019t woken up fully if you haven\u2019t sipped Assam tea\u2019. Grown on the rolling plains by the Brahmaputra River, Assam tea is iconic \u2014 strong, malty, complex.",
  variants: [
    {
      id: "ctc",
      name: "CTC",
      image: "/products/tea/ctc.png",
      description:
        "Harvested from the legendary tea gardens of Assam, our premium CTC tea offers a bold, full-bodied infusion with timeless elegance in every cup. Expertly crafted to preserve its rich aroma and exceptional character, every cup is a tribute to generations of craftsmanship \u2014 delivering an experience of uncompromising quality and refined indulgence.",
    },
    {
      id: "orthodox",
      name: "Orthodox",
      image: "/products/tea/orthodox.png",
      description:
        "Handpicked from the celebrated tea gardens of Assam, our premium Assam Orthodox tea is a celebration of whole-leaf excellence, offering a smooth, aromatic cup with remarkable depth and character \u2014 a refined balance of rich maltiness and delicate floral notes. An enduring expression of heritage, crafted for the discerning connoisseur.",
    },
    {
      id: "green-tea",
      name: "Green Tea",
      image: "/products/tea/green-tea.png",
      description:
        "Inspired by nature and perfected through expert craftsmanship, our Assam Green Tea offers a light, refreshing cup with delicate vegetal notes and a clean finish. A delicate brew that awakens the senses with refreshing clarity and understated elegance \u2014 an invitation to savour purity, balance, and effortless sophistication.",
    },
  ],
};
